import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { WalletCard } from '@/components/playground/wallet-card'
import { RedeemDialog } from '@/components/playground/redeem-dialog'
import { PushButton } from '@/components/ui/push-button'

export const Route = createFileRoute('/_app/settings/playground')({
  component: PlaygroundPage,
})

function PlaygroundPage() {
  // Simulator State
  const [balance, setBalance] = useState(60.0)
  const [subscriptionValue, setSubscriptionValue] = useState(49.9)
  const [isSubscriber, setIsSubscriber] = useState(true)

  // Widget State
  const [isRedeemOpen, setIsRedeemOpen] = useState(false)

  const handleRedeemSuccess = () => {
    // Simulate transaction
    setTimeout(() => {
      setBalance((prev) =>
        Math.max(0, prev - (isSubscriber ? Math.min(prev, subscriptionValue) : prev))
      )
    }, 500)
  }

  return (
    <div className="max-w-[1600px]">
      <div className="flex flex-col lg:flex-row gap-8 items-start justify-center">
        {/* Controls Panel */}
        <div className="w-full lg:w-[350px] shrink-0">
          <Card className="sticky top-6 border-0 shadow-lg bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle>Member Experience Simulator</CardTitle>
              <CardDescription>See what your customers see in real-time.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Customer Role</Label>
                <div className="flex items-center justify-between border p-3 rounded-lg bg-background">
                  <span className="text-sm font-medium">Is Subscriber?</span>
                  <Switch checked={isSubscriber} onCheckedChange={setIsSubscriber} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Wallet Balance (R$)</Label>
                <Input
                  type="number"
                  value={balance}
                  onChange={(e) => setBalance(Number(e.target.value))}
                  step="0.01"
                />
              </div>

              {isSubscriber && (
                <div className="space-y-2">
                  <Label>Subscription Cost (R$)</Label>
                  <Input
                    type="number"
                    value={subscriptionValue}
                    onChange={(e) => setSubscriptionValue(Number(e.target.value))}
                    step="0.01"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>New 3D Button Action</Label>
                <div className="p-4 border rounded-lg bg-muted/20 flex justify-center">
                  <PushButton onClick={() => alert('3D Button Clicked!')}>Push Me 3D</PushButton>
                </div>
              </div>

              <Separator />

              <div className="text-xs text-muted-foreground p-3 bg-muted rounded border border-dashed">
                <p className="font-semibold mb-1">Scenario Logic:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Eligible for Redeem: {balance > 0 ? 'Yes' : 'No'}</li>
                  <li>
                    Full Month Free: {isSubscriber && balance >= subscriptionValue ? 'Yes' : 'No'}
                  </li>
                  <li>
                    Partial Discount:{' '}
                    {isSubscriber && balance > 0 && balance < subscriptionValue ? 'Yes' : 'No'}
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Simulated View (Phone) */}
        <div className="flex-1 flex justify-center items-center bg-zinc-100 dark:bg-zinc-950/50 p-8 rounded-[3rem] min-h-[calc(100vh-100px)] relative overflow-hidden border border-zinc-200 dark:border-zinc-800 shadow-inner">
          {/* Background Ambient Light */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-blue-500/10 via-purple-500/10 to-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

          <div className="w-full max-w-[375px] bg-white dark:bg-black border-[8px] border-zinc-900 dark:border-zinc-800 shadow-2xl rounded-[50px] overflow-hidden flex flex-col relative h-[812px] z-10 select-none">
            {/* Dynamic Island Area */}
            <div className="h-14 w-full bg-transparent z-20 absolute top-0 flex justify-center pt-2 pointer-events-none">
              <div className="w-28 h-8 bg-black rounded-full" />
            </div>

            {/* Status Bar Time */}
            <div className="absolute top-0 w-full h-14 flex justify-between items-center px-8 z-20 text-[13px] font-semibold text-black dark:text-white pointer-events-none">
              <span>9:41</span>
              <div className="flex gap-1.5 opacity-80">
                <div className="w-4 h-2.5 bg-current rounded-[2px]" />
              </div>
            </div>

            <div className="flex-1 px-6 pt-16 pb-8 flex flex-col gap-6 overflow-y-auto scrollbar-hide bg-zinc-50 dark:bg-zinc-950 relative">
              {/* Header Minimal */}
              <div className="flex justify-between items-center py-2">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-full shadow-md ring-2 ring-white dark:ring-black" />
                  <div className="space-y-1">
                    <div className="h-3 w-16 bg-zinc-200 dark:bg-zinc-800 rounded animate-pulse" />
                    <div className="h-2 w-10 bg-zinc-100 dark:bg-zinc-900 rounded animate-pulse" />
                  </div>
                </div>
                <div className="h-8 w-8 rounded-full border border-zinc-200 dark:border-zinc-800 flex items-center justify-center">
                  <span className="sr-only">Notifications</span>
                  <div className="w-4 h-4 rounded-full border-2 border-zinc-300 dark:border-zinc-700" />
                </div>
              </div>

              {/* The Actual Component */}
              <div className="transform transition-all duration-300">
                <WalletCard
                  balance={balance}
                  subscriptionValue={subscriptionValue}
                  isSubscriber={isSubscriber}
                  onRedeem={() => setIsRedeemOpen(true)}
                />
              </div>

              {/* Fake List Clean */}
              <div className="space-y-5 pt-4">
                <div className="flex justify-between items-center px-1">
                  <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">
                    Ultimas atividades
                  </p>
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-1 rounded-full">
                    +12% este mês
                  </span>
                </div>
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center p-4 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-100 dark:border-zinc-800 shadow-sm"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-lg">
                        {i === 1 ? '🎉' : i === 2 ? '💳' : '🎁'}
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="h-3 w-24 bg-zinc-100 dark:bg-zinc-800 rounded" />
                        <div className="h-2 w-14 bg-zinc-50 dark:bg-zinc-900 rounded" />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="h-3 w-12 bg-zinc-100 dark:bg-zinc-800 rounded ml-auto mb-1" />
                      <div className="h-2 w-8 bg-zinc-50 dark:bg-zinc-900 rounded ml-auto" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Minimal */}
            <div className="h-[84px] bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border-t border-zinc-100 dark:border-zinc-800 flex justify-around items-start pt-4 px-6 z-20 absolute bottom-0 w-full">
              <div className="w-6 h-6 rounded-full bg-zinc-900 dark:bg-white" />
              <div className="w-6 h-6 rounded-full bg-zinc-300 dark:bg-zinc-700" />
              <div className="w-6 h-6 rounded-full bg-zinc-300 dark:bg-zinc-700" />
              <div className="w-6 h-6 rounded-full bg-zinc-300 dark:bg-zinc-700" />
            </div>
          </div>
        </div>
      </div>

      <RedeemDialog
        open={isRedeemOpen}
        onOpenChange={setIsRedeemOpen}
        balance={balance}
        subscriptionValue={subscriptionValue}
        isSubscriber={isSubscriber}
        onSuccess={handleRedeemSuccess}
      />
    </div>
  )
}
